/**
 * End-to-end encryption for DMs and group DMs, built on MLS (RFC 9420).
 *
 * Every device is an MLS member; every DM channel is an MLS group. The server acts only
 * as the MLS Delivery Service: it stores KeyPackages, orders commits per channel by
 * epoch, and relays opaque ciphertext. It never holds a private key or plaintext.
 *
 * These functions are pure and storage-agnostic so the same code runs in browsers
 * (IndexedDB persistence) and in a future React Native app.
 */
import { createApplicationMessage, createCommit, createGroup, createGroupInfoWithExternalPubAndRatchetTree, decodeGroupState, decodeMlsMessage, defaultCapabilities, defaultLifetime, emptyPskIndex, encodeGroupState, encodeMlsMessage, generateKeyPackageWithKey, getCiphersuiteFromName, getCiphersuiteImpl, joinGroup, joinGroupExternal, mlsExporter, processPrivateMessage, processPublicMessage, zeroOutUint8Array, } from 'ts-mls';
import { defaultClientConfig } from 'ts-mls/clientConfig.js';
import { decodeKeyPackage, encodeKeyPackage, makeKeyPackageRef } from 'ts-mls/keyPackage.js';
import { decryptSenderData } from 'ts-mls/privateMessage.js';
import { ratchetTreeFromExtension } from 'ts-mls/groupInfo.js';
import { bytesEqual, fromBase64, toBase64, toHex, utf8 } from './encoding.js';
/** Hybrid post-quantum (X25519 + ML-KEM-768) key exchange, AES-256-GCM, Ed25519 signatures. */
export const CIPHERSUITE = 'MLS_256_XWING_AES256GCM_SHA512_Ed25519';
let implPromise;
export function getSuite() {
    implPromise ??= getCiphersuiteImpl(getCiphersuiteFromName(CIPHERSUITE));
    return implPromise;
}
/** Generate the long-term signature key pair for a new device. */
export async function generateSignatureKeyPair() {
    const cs = await getSuite();
    const kp = await cs.signature.keygen();
    return { publicKey: kp.publicKey, privateKey: kp.signKey };
}
function makeCredential(id) {
    return {
        credentialType: 'basic',
        identity: utf8.encode(JSON.stringify({ u: id.userId, d: id.deviceId })),
    };
}
export function parseCredential(credential) {
    if (credential.credentialType !== 'basic')
        return null;
    try {
        const v = JSON.parse(utf8.decode(credential.identity));
        if (typeof v.u === 'string' && typeof v.d === 'string')
            return { userId: v.u, deviceId: v.d };
    }
    catch {
        /* fall through */
    }
    return null;
}
export function makeClientConfig(resolveKey) {
    const authService = {
        async validateCredential(credential, signaturePublicKey) {
            const member = parseCredential(credential);
            if (!member)
                return false;
            const known = await resolveKey(member);
            return known !== null && bytesEqual(known, signaturePublicKey);
        },
    };
    return { ...defaultClientConfig, authService };
}
export async function generateKeyPackages(identity, count) {
    const cs = await getSuite();
    const publish = [];
    const store = [];
    for (let i = 0; i < count; i++) {
        const kp = await generateKeyPackageWithKey(makeCredential(identity), defaultCapabilities(), defaultLifetime, [], { signKey: identity.signaturePrivateKey, publicKey: identity.signaturePublicKey }, cs);
        const encoded = encodeMlsMessage({
            version: 'mls10',
            wireformat: 'mls_key_package',
            keyPackage: kp.publicPackage,
        });
        publish.push(toBase64(encoded));
        store.push({
            ref: toHex(await makeKeyPackageRef(kp.publicPackage, cs.hash)),
            publicPackage: encodeKeyPackage(kp.publicPackage),
            privatePackage: {
                initPrivateKey: toBase64(kp.privatePackage.initPrivateKey),
                hpkePrivateKey: toBase64(kp.privatePackage.hpkePrivateKey),
                signaturePrivateKey: toBase64(kp.privatePackage.signaturePrivateKey),
            },
        });
    }
    return { publish, store };
}
function decodePublishedKeyPackage(b64) {
    const decoded = decodeMlsMessage(fromBase64(b64), 0)?.[0];
    if (!decoded || decoded.wireformat !== 'mls_key_package')
        throw new Error('not a key package');
    return decoded.keyPackage;
}
/** Inspect a published key package (e.g. to check it matches the device it was claimed for). */
export function inspectKeyPackage(b64) {
    const kp = decodePublishedKeyPackage(b64);
    const id = parseCredential(kp.leafNode.credential);
    if (!id)
        throw new Error('key package has an invalid credential');
    return { ...id, signaturePublicKey: kp.leafNode.signaturePublicKey };
}
export function groupEpoch(g) {
    return Number(g.state.groupContext.epoch);
}
export function groupMembers(g) {
    const out = [];
    g.state.ratchetTree.forEach((node, nodeIndex) => {
        if (node && node.nodeType === 'leaf') {
            const id = parseCredential(node.leaf.credential);
            if (id)
                out.push({
                    ...id,
                    leafIndex: nodeIndex / 2,
                    signaturePublicKey: node.leaf.signaturePublicKey,
                });
        }
    });
    return out;
}
/** A one-off key package for this device's own leaf (creating or externally joining a group). */
async function ownKeyPackage(identity, cs) {
    return generateKeyPackageWithKey(makeCredential(identity), defaultCapabilities(), defaultLifetime, [], { signKey: identity.signaturePrivateKey, publicKey: identity.signaturePublicKey }, cs);
}
export async function createDmGroup(identity, channelId, config) {
    const cs = await getSuite();
    const own = await ownKeyPackage(identity, cs);
    const state = await createGroup(utf8.encode(`dm:${channelId}`), own.publicPackage, own.privatePackage, [], cs, config);
    return { state };
}
/**
 * Add and/or remove devices in one commit. Key packages are validated against the
 * resolver before being added. The returned group state must only be persisted once
 * the server has accepted the commit (otherwise discard it and retry after syncing).
 */
export async function commitMembershipChange(g, change, resolveKey) {
    const cs = await getSuite();
    const proposals = [];
    for (const b64 of change.addKeyPackages ?? []) {
        const kp = decodePublishedKeyPackage(b64);
        const id = parseCredential(kp.leafNode.credential);
        const known = id ? await resolveKey(id) : null;
        if (!known || !bytesEqual(known, kp.leafNode.signaturePublicKey)) {
            throw new Error('key package does not match a known device');
        }
        proposals.push({ proposalType: 'add', add: { keyPackage: kp } });
    }
    const members = groupMembers(g);
    for (const dev of change.removeDevices ?? []) {
        const m = members.find((x) => x.userId === dev.userId && x.deviceId === dev.deviceId);
        if (m)
            proposals.push({ proposalType: 'remove', remove: { removed: m.leafIndex } });
    }
    const res = await createCommit({ state: g.state, cipherSuite: cs }, { extraProposals: proposals, ratchetTreeExtension: true });
    return {
        group: { state: res.newState },
        zeroConsumed: () => res.consumed.forEach(zeroOutUint8Array),
        commit: toBase64(encodeMlsMessage(res.commit)),
        welcome: res.welcome
            ? toBase64(encodeMlsMessage({ version: 'mls10', wireformat: 'mls_welcome', welcome: res.welcome }))
            : undefined,
    };
}
/** Empty commit that rotates this device's keys (post-compromise security). */
export function commitKeyUpdate(g, resolveKey) {
    return commitMembershipChange(g, {}, resolveKey);
}
/**
 * Join a group from a Welcome. `findKeyPackage` looks up the private half of the key
 * package the Welcome was encrypted to (by hex ref). Returns null if the Welcome was
 * not addressed to any of our key packages.
 */
export async function joinFromWelcome(welcomeB64, findKeyPackage, config) {
    const cs = await getSuite();
    const decoded = decodeMlsMessage(fromBase64(welcomeB64), 0)?.[0];
    if (!decoded || decoded.wireformat !== 'mls_welcome')
        throw new Error('not a welcome message');
    for (const secret of decoded.welcome.secrets) {
        const ref = toHex(secret.newMember);
        const stored = await findKeyPackage(ref);
        if (!stored)
            continue;
        const pub = decodeKeyPackage(stored.publicPackage, 0)?.[0];
        if (!pub)
            throw new Error('corrupt stored key package');
        const priv = {
            initPrivateKey: fromBase64(stored.privatePackage.initPrivateKey),
            hpkePrivateKey: fromBase64(stored.privatePackage.hpkePrivateKey),
            signaturePrivateKey: fromBase64(stored.privatePackage.signaturePrivateKey),
        };
        const state = await joinGroup(decoded.welcome, pub, priv, emptyPskIndex, cs, undefined, undefined, config);
        return { group: { state }, usedRef: ref };
    }
    return null;
}
/**
 * A signed snapshot of the group at its current epoch (ratchet tree and external public
 * key included). Published to the server after every commit, it lets a participant's
 * device join on its own (an MLS external commit) without waiting for anyone to add it.
 */
export async function exportGroupInfo(g) {
    const cs = await getSuite();
    const groupInfo = await createGroupInfoWithExternalPubAndRatchetTree(g.state, [], cs);
    return toBase64(encodeMlsMessage({ version: 'mls10', wireformat: 'mls_group_info', groupInfo }));
}
/**
 * Join a group from its published GroupInfo with an external commit. The GroupInfo's
 * signer and every leaf in its tree are checked against the device directory. If this
 * device already has a (stale) leaf in the group, the commit replaces it. The returned
 * state must only be persisted once the server accepts the commit.
 */
export async function joinExternal(identity, groupInfoB64, config) {
    const cs = await getSuite();
    const decoded = decodeMlsMessage(fromBase64(groupInfoB64), 0)?.[0];
    if (!decoded || decoded.wireformat !== 'mls_group_info')
        throw new Error('not a group info');
    const tree = ratchetTreeFromExtension(decoded.groupInfo) ?? [];
    const resync = tree.some((n) => {
        if (!n || n.nodeType !== 'leaf')
            return false;
        const id = parseCredential(n.leaf.credential);
        return id?.deviceId === identity.deviceId && id.userId === identity.userId;
    });
    const own = await ownKeyPackage(identity, cs);
    const res = await joinGroupExternal(decoded.groupInfo, own.publicPackage, own.privatePackage, resync, cs, undefined, config);
    return {
        group: { state: res.newState },
        commit: toBase64(encodeMlsMessage({
            version: 'mls10',
            wireformat: 'mls_public_message',
            publicMessage: res.publicMessage,
        })),
    };
}
/* ---------------------------------------------------------------- messages */
export async function encryptApplicationMessage(g, plaintext) {
    const cs = await getSuite();
    const epoch = groupEpoch(g);
    const res = await createApplicationMessage(g.state, plaintext, cs);
    res.consumed.forEach(zeroOutUint8Array);
    const bytes = encodeMlsMessage({
        version: 'mls10',
        wireformat: 'mls_private_message',
        privateMessage: res.privateMessage,
    });
    return { group: { state: res.newState }, ciphertext: toBase64(bytes), epoch };
}
/** Process an application message or a commit created by another member. */
export async function processIncoming(g, ciphertextB64) {
    const cs = await getSuite();
    const decoded = decodeMlsMessage(fromBase64(ciphertextB64), 0)?.[0];
    if (decoded?.wireformat === 'mls_public_message') {
        // Only external commits (a device joining on its own) travel unencrypted.
        if (decoded.publicMessage.content.sender.senderType !== 'new_member_commit')
            throw new Error('unexpected unencrypted MLS message');
        const res = await processPublicMessage(g.state, decoded.publicMessage, emptyPskIndex, cs);
        res.consumed.forEach(zeroOutUint8Array);
        return {
            kind: 'commit',
            group: { state: res.newState },
            removedSelf: res.newState.groupActiveState.kind !== 'active',
        };
    }
    if (!decoded || decoded.wireformat !== 'mls_private_message')
        throw new Error('not an MLS private message');
    const sender = await resolveSender(g, decoded.privateMessage, cs);
    const res = await processPrivateMessage(g.state, decoded.privateMessage, emptyPskIndex, cs);
    res.consumed.forEach(zeroOutUint8Array);
    if (res.kind === 'applicationMessage') {
        return { kind: 'application', group: { state: res.newState }, plaintext: res.message, sender };
    }
    const removedSelf = res.newState.groupActiveState.kind !== 'active';
    return { kind: 'commit', group: { state: res.newState }, removedSelf };
}
/**
 * Identify the (MLS-authenticated) sender of a private message: decrypt the sender
 * data to get the leaf index, then read that leaf's credential from the tree of the
 * message's epoch. processPrivateMessage verifies the signature against the same leaf.
 */
async function resolveSender(g, pm, cs) {
    let secret;
    let tree;
    if (pm.epoch === g.state.groupContext.epoch) {
        secret = g.state.keySchedule.senderDataSecret;
        tree = g.state.ratchetTree;
    }
    else {
        const hist = g.state.historicalReceiverData.get(pm.epoch);
        if (!hist)
            return null;
        secret = hist.senderDataSecret;
        tree = hist.ratchetTree;
    }
    const senderData = await decryptSenderData(pm, secret, cs);
    if (!senderData)
        return null;
    const node = tree[senderData.leafIndex * 2];
    if (!node || node.nodeType !== 'leaf')
        return null;
    return parseCredential(node.leaf.credential);
}
/**
 * Derive a secret from the group's current epoch (RFC 9420 exporter). Every member
 * computes the same value locally; it changes whenever membership changes.
 */
export async function exportGroupSecret(g, label, context, length = 32) {
    const cs = await getSuite();
    return mlsExporter(g.state.keySchedule.exporterSecret, label, context, length, cs);
}
/* ---------------------------------------------------------- persistence */
export function serializeGroup(g) {
    return encodeGroupState(g.state);
}
export function deserializeGroup(bytes, config) {
    const decoded = decodeGroupState(bytes, 0)?.[0];
    if (!decoded)
        throw new Error('corrupt group state');
    return { state: { ...decoded, clientConfig: config } };
}
//# sourceMappingURL=mls.js.map